import React, { useState, useEffect } from 'react';
import { Activity, Shield, Zap } from 'lucide-react';
import { UserRole } from '@/types';
import KPICard from '@/components/KPICard';
import FleetStatusChart from '@/components/FleetStatusChart';
import WasteScorecardChart from '@/components/WasteScorecardChart';
import FALLOUTRiskChart from '@/components/FALLOUTRiskChart';
import AlertsTable from '@/components/AlertsTable';
import { 
  kpiCards, 
  fleetStatus, 
  wasteMetrics, 
  pipelineRisks, 
  recentAlerts 
} from '@/data/mockData';

interface CommandCenterProps {
  currentRole: UserRole;
}

const CommandCenter: React.FC<CommandCenterProps> = ({ currentRole }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    const timer = setTimeout(() => setIsLoaded(true), 400);
    return () => clearTimeout(timer);
  }, [currentRole]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-navy-primary flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-gold-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-text-muted">Loading Command Center…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-transition ${isLoaded ? 'active' : ''} p-6 lg:p-8 space-y-6 max-w-[1600px] mx-auto`}>
      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center space-x-3 mb-1">
          <h1 className="text-2xl font-bold text-text-heading">Command Center</h1>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            Live
          </span>
        </div>
        <p className="text-sm text-text-muted">
          Unified monitoring for AFN AI systems
          {currentRole && <span className="text-text-muted/60"> · {currentRole} view</span>}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiCards.map((kpi) => (
          <KPICard key={kpi.id} {...kpi} />
        ))}
      </div>

      {/* Kronos Briefing + Fleet Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kronos Briefing */}
        <div className="bg-navy-card border border-navy-border rounded-xl p-6">
          <div className="flex items-center space-x-2 mb-5">
            <div className="p-1.5 rounded-md bg-gold-primary/10">
              <Zap size={16} className="text-gold-primary" />
            </div>
            <h3 className="text-base font-semibold text-text-heading">Kronos Briefing</h3>
          </div>
          
          <div className="space-y-4">
            <div className="border-l-2 border-gold-primary/60 pl-4">
              <h4 className="text-sm font-medium text-text-heading mb-1">Today's Operations</h4>
              <p className="text-xs text-text-muted leading-relaxed">
                Processing volume up 15% — 2,847 loans in pipeline. Otto match rate slightly decreased but within range.
              </p>
            </div>
            
            <div className="border-l-2 border-blue-500/60 pl-4">
              <h4 className="text-sm font-medium text-text-heading mb-1">Key Metrics</h4>
              <ul className="text-xs text-text-muted space-y-0.5 leading-relaxed">
                <li>• Cycle time reduced 4.2% (18.5 days avg)</li>
                <li>• Fleet health at 94% operational</li>
                <li>• 42 active agents processing workload</li>
              </ul>
            </div>
            
            <div className="border-l-2 border-amber-500/60 pl-4">
              <h4 className="text-sm font-medium text-text-heading mb-1">Attention Required</h4>
              <ul className="text-xs text-text-muted space-y-0.5 leading-relaxed">
                <li>• Otto elevated latency — Engineering investigating</li>
                <li>• FALLOUT prediction accuracy below target</li>
                <li>• Quality defect rate needs reduction (6 vs target 3)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Fleet Status */}
        <FleetStatusChart data={fleetStatus} />
      </div>

      {/* Waste Scorecard + FALLOUT Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WasteScorecardChart data={wasteMetrics} />
        <FALLOUTRiskChart data={pipelineRisks} />
      </div>

      {/* Alerts */}
      <AlertsTable alerts={recentAlerts} />

      {/* Role-specific panels */}
      {currentRole === 'Executive' && (
        <div className="bg-navy-card border border-navy-border rounded-xl p-6">
          <div className="flex items-center space-x-2 mb-5">
            <div className="p-1.5 rounded-md bg-gold-primary/10">
              <Shield size={16} className="text-gold-primary" />
            </div>
            <h3 className="text-base font-semibold text-text-heading">Executive Summary</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-xs font-semibold text-gold-primary uppercase tracking-wider mb-2">Strategic Initiatives</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• AI automation increasing processing efficiency</li>
                <li>• Channel expansion showing positive metrics</li>
                <li>• Compliance automation reducing oversight</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gold-primary uppercase tracking-wider mb-2">Market Position</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• Processing capacity ahead of industry avg</li>
                <li>• Tech stack competitive advantage maintained</li>
                <li>• Customer satisfaction trending positive</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gold-primary uppercase tracking-wider mb-2">Investment ROI</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• AI reducing operational costs by 23%</li>
                <li>• Cycle time saving $1.2M annually</li>
                <li>• Error reduction preventing $800K in rework</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {currentRole === 'IT/Dev' && (
        <div className="bg-navy-card border border-navy-border rounded-xl p-6">
          <div className="flex items-center space-x-2 mb-5">
            <div className="p-1.5 rounded-md bg-blue-500/10">
              <Activity size={16} className="text-blue-400" />
            </div>
            <h3 className="text-base font-semibold text-text-heading">Technical Operations</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">System Health</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• 42 agents deployed in production</li>
                <li>• 98.5% average uptime across services</li>
                <li>• API response times within SLA</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">Performance</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• DB queries optimized, 40% faster</li>
                <li>• Cache hit ratio at 94%</li>
                <li>• Memory utilization stable at 68%</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">Security</h4>
              <ul className="text-xs text-text-muted space-y-1 leading-relaxed">
                <li>• Zero security incidents this month</li>
                <li>• All systems patched and current</li>
                <li>• Pen test scheduled next week</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenter;
